import { useMemo } from 'react'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { DomainFilters } from './DomainFilters'
import { WorkflowRow } from './WorkflowRow'
import type { Workflow } from '@/types/dashboard'

interface Props {
  workflows: Workflow[]
  domains: string[]
  domainFilter: string
  proposingId: string | null
  onDomainFilter: (d: string) => void
  onPropose: (id: string) => void
  onDetect: () => void
  onCopy: (id: string) => void
  embedded?: boolean
}

export function WorkflowList({
  workflows,
  domains,
  domainFilter,
  proposingId,
  onDomainFilter,
  onPropose,
  onDetect,
  onCopy,
  embedded
}: Props) {
  const items = useMemo(
    () =>
      domainFilter === 'all' ? workflows : workflows.filter((w) => w.domain === domainFilter),
    [workflows, domainFilter]
  )

  const body = (
    <>
      {!embedded ? (
        <div className="section-head">
          <div>
            <h2>Learned workflows</h2>
            <p>Cross-session patterns with sequence alignment.</p>
          </div>
          <div className="section-actions">
            <Button variant="primary" size="sm" onClick={onDetect}>
              Re-detect
            </Button>
          </div>
        </div>
      ) : (
        <div className="section-actions" style={{ marginBottom: 12 }}>
          <Button variant="primary" size="sm" onClick={onDetect}>
            Re-detect
          </Button>
        </div>
      )}
      <DomainFilters domains={domains} active={domainFilter} onChange={onDomainFilter} />
      <div className="stack">
        {!items.length ? (
          <EmptyState
            title="No workflows yet"
            body="Capture activity from the extension, then run Detect — or seed a template."
            actionLabel="Detect patterns"
            onAction={onDetect}
          />
        ) : (
          items.map((w) => (
            <WorkflowRow
              key={w.id}
              workflow={w}
              proposing={proposingId === w.id}
              onPropose={onPropose}
              onCopy={onCopy}
            />
          ))
        )}
      </div>
    </>
  )

  if (embedded) return <div className="embed-panel">{body}</div>
  return (
    <section className="block" id="learned">
      {body}
    </section>
  )
}
